import { createApi } from '@reduxjs/toolkit/query/react';

import { REACT_APP_BE_API_URL } from '#shared';

import {
  asQueryFnResult,
  fetchWithAuthBaseQuery,
  requestJsonWithAuth,
} from '#shared/api';

import {
  applyIfNewer,
  checkValue,
  reportSettingsDrift,
  settleWrite,
} from '../lib';

import {
  parseEchoQueues,
  parseGlobalSettings,
  parseUserParameters,
  readSettingRejection,
} from './schemas';

import type {
  EchoQueue,
  GlobalSettings,
  SettingsOrigin,
  UserParameters,
} from '../model';

export interface SettingWrite {
  key: string;
  value: unknown;
}

type SettingWriteKind = { kind: 'set'; value: unknown } | { kind: 'reset' };

const GLOBAL_PATH = 'api/settings/';
const ECHO_QUEUES_PATH = 'api/admin/echo/queues';
const USER_PATH = 'api/me/parameters/';

const writeSetting = <T extends { ts: number }>(
  path: string,
  key: string,
  parse: (raw: unknown, origin: SettingsOrigin) => T,
  write: SettingWriteKind,
) =>
  asQueryFnResult(async () => {
    const isReset = write.kind === 'reset';
    try {
      const raw = await requestJsonWithAuth<unknown>(
        `${REACT_APP_BE_API_URL}${path}${encodeURIComponent(key)}`,
        {
          method: isReset ? 'DELETE' : 'POST',
          expectedForbidden: true,
          ...(write.kind === 'set' ? { json: { value: write.value } } : {}),
        },
      );
      return parse(raw, 'response');
    } catch (error) {
      const violations = readSettingRejection(error);
      if (
        violations &&
        write.kind === 'set' &&
        checkValue(key, write.value) === 'ok'
      ) {
        reportSettingsDrift({
          direction: 'rejected',
          key,
          value: write.value,
          violations,
        });
      }
      throw error;
    }
  });

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: fetchWithAuthBaseQuery,

  endpoints: build => ({
    getGlobalSettings: build.query<GlobalSettings, void>({
      queryFn: () =>
        asQueryFnResult(async () =>
          parseGlobalSettings(
            await requestJsonWithAuth<unknown>(
              `${REACT_APP_BE_API_URL}api/settings`,
            ),
            'response',
          ),
        ),
      merge: (current, incoming) => applyIfNewer(incoming)(current),
    }),

    getUserParameters: build.query<UserParameters, void>({
      queryFn: () =>
        asQueryFnResult(async () =>
          parseUserParameters(
            await requestJsonWithAuth<unknown>(
              `${REACT_APP_BE_API_URL}api/me/parameters`,
            ),
            'response',
          ),
        ),
      merge: (current, incoming) => applyIfNewer(incoming)(current),
    }),

    setGlobalSetting: build.mutation<GlobalSettings, SettingWrite>({
      queryFn: ({ key, value }) =>
        writeSetting(GLOBAL_PATH, key, parseGlobalSettings, {
          kind: 'set',
          value,
        }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        settleWrite(queryFulfilled, data =>
          dispatch(
            settingsApi.util.updateQueryData(
              'getGlobalSettings',
              undefined,
              applyIfNewer(data),
            ),
          ),
        ),
    }),

    resetGlobalSetting: build.mutation<GlobalSettings, string>({
      queryFn: key =>
        writeSetting(GLOBAL_PATH, key, parseGlobalSettings, { kind: 'reset' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        settleWrite(queryFulfilled, data =>
          dispatch(
            settingsApi.util.updateQueryData(
              'getGlobalSettings',
              undefined,
              applyIfNewer(data),
            ),
          ),
        ),
    }),

    getEchoQueues: build.query<EchoQueue[], void>({
      queryFn: () =>
        asQueryFnResult(async () =>
          parseEchoQueues(
            await requestJsonWithAuth<unknown>(
              `${REACT_APP_BE_API_URL}${ECHO_QUEUES_PATH}`,
            ),
          ),
        ),
    }),

    setUserParameter: build.mutation<UserParameters, SettingWrite>({
      queryFn: ({ key, value }) =>
        writeSetting(USER_PATH, key, parseUserParameters, {
          kind: 'set',
          value,
        }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        settleWrite(queryFulfilled, data =>
          dispatch(
            settingsApi.util.updateQueryData(
              'getUserParameters',
              undefined,
              applyIfNewer(data),
            ),
          ),
        ),
    }),

    resetUserParameter: build.mutation<UserParameters, string>({
      queryFn: key =>
        writeSetting(USER_PATH, key, parseUserParameters, { kind: 'reset' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        settleWrite(queryFulfilled, data =>
          dispatch(
            settingsApi.util.updateQueryData(
              'getUserParameters',
              undefined,
              applyIfNewer(data),
            ),
          ),
        ),
    }),
  }),
});

export const {
  useGetEchoQueuesQuery,
  useGetGlobalSettingsQuery,
  useGetUserParametersQuery,
  useSetGlobalSettingMutation,
  useResetGlobalSettingMutation,
  useSetUserParameterMutation,
  useResetUserParameterMutation,
} = settingsApi;
